// src/admin/pages/PendingOrderListPage.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEdit, FaTrashAlt, FaSearch, FaPlus } from 'react-icons/fa';
import Notification from '../components/Notification/Notification';
import OrderDetailsModal from '../components/OrderDetails/ViewOrderDetails'; // NEW: Import the modal component

// Define interfaces for Order and related nested objects
interface Customer {
  id: number;
  name: string;
}

interface ProductItem { // NEW: Interface for products within an order
  productId: number;
  productName: string;
  quantity: number;
  pricePerUnit: number;
}

interface Order {
  id: number;
  customer: Customer;
  orderDate: string;
  totalAmount: number;
  orderStatus: 'PENDING' | 'PROCESSING' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED' | 'REFUND';
  paymentStatus: 'PAID' | 'UNPAID' | 'REFUND_PENDING' | 'REFUNDED';
  shippingStatus: 'READY_FOR_DISPATCH' | 'IN_TRANSIT' | 'OUT_FOR_DELIVERY' | 'DELIVERED';
  deliveryAddress: string; // NEW: Added delivery address
  products: ProductItem[]; // NEW: Added products list
}

const PendingOrder: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Notification state
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info'; } | null>(null);

  // Function to show notification
  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ message, type });
  };

  // --- Search, Sort & Pagination States ---
  const [searchTerm, setSearchTerm] = useState<string>('');
  // Removed filterOrderStatus as this page is specifically for PENDING orders
  const [filterPaymentStatus, setFilterPaymentStatus] = useState<string>('all');
  const [filterShippingStatus, setFilterShippingStatus] = useState<string>('all');
  const [sortColumn, setSortColumn] = useState<string>('orderDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc'); // Default to newest orders first
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [ordersPerPage] = useState<number>(10);

  // --- Delete Confirmation Modal States ---
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [orderToDelete, setOrderToDelete] = useState<Order | null>(null);

  // --- Order Details Modal States --- // NEW
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Dummy Data for demonstration, filtered to include only PENDING orders
  const dummyPendingOrders: Order[] = useMemo(() => ([
    {
      id: 4, customer: { id: 104, name: 'Bob Williams' }, orderDate: '2025-07-03T16:45:00', totalAmount: 45.75,
      orderStatus: 'PENDING', paymentStatus: 'UNPAID', shippingStatus: 'READY_FOR_DISPATCH',
      deliveryAddress: '101 Elm St, Smallville, USA 54321',
      products: [
        { productId: 401, productName: 'T-shirt (Large)', quantity: 3, pricePerUnit: 15.25 }
      ]
    },
    {
      id: 8, customer: { id: 108, name: 'Frank White' }, orderDate: '2025-07-04T18:00:00', totalAmount: 90.00,
      orderStatus: 'PENDING', paymentStatus: 'REFUND_PENDING', shippingStatus: 'READY_FOR_DISPATCH',
      deliveryAddress: '505 Cedar Ct, Lakeview, USA 88990',
      products: [
        { productId: 801, productName: 'Headphones', quantity: 1, pricePerUnit: 90.00 }
      ]
    },
    {
      id: 11, customer: { id: 111, name: 'Ivy Queen' }, orderDate: '2025-07-05T11:00:00', totalAmount: 200.00,
      orderStatus: 'PENDING', paymentStatus: 'UNPAID', shippingStatus: 'READY_FOR_DISPATCH',
      deliveryAddress: '808 Fir Dr, Valley City, USA 77889',
      products: [
        { productId: 1101, productName: 'Gaming Keyboard', quantity: 1, pricePerUnit: 200.00 }
      ]
    },
    // Add more pending orders if needed for testing
  ]), []);

  // Simulate fetching data
  useEffect(() => {
    setLoading(true);
    // In a real application, you would fetch data from your backend here,
    // explicitly requesting only pending orders, e.g.:
    // fetch('http://localhost:8282/api/orders?status=PENDING')
    //   .then(response => response.json())
    //   .then(data => {
    //     setOrders(data);
    //     setLoading(false);
    //   })
    //   .catch(err => {
    //     showNotification(`Failed to fetch pending orders: ${err.message}`, 'error');
    //     setLoading(false);
    //   });

    // For now, use filtered dummy data after a small delay
    const timer = setTimeout(() => {
      setOrders(dummyPendingOrders);
      setLoading(false);
    }, 500); // Simulate network delay
    return () => clearTimeout(timer);
  }, [dummyPendingOrders]);

  // --- Search, Filter & Sort Logic ---
  const filteredAndSortedOrders = useMemo(() => {
    let currentOrders = [...orders];

    // 1. Filter by Search Term
    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      currentOrders = currentOrders.filter(order =>
        order.id.toString().includes(lowerCaseSearchTerm) ||
        order.customer.name.toLowerCase().includes(lowerCaseSearchTerm) ||
        order.paymentStatus.toLowerCase().includes(lowerCaseSearchTerm) ||
        order.shippingStatus.toLowerCase().includes(lowerCaseSearchTerm) ||
        order.totalAmount.toFixed(2).includes(lowerCaseSearchTerm) ||
        order.products.some(p => p.productName.toLowerCase().includes(lowerCaseSearchTerm)) // Search by product name
      );
    }

    // This page is specifically for PENDING orders, so no need for orderStatus filter.
    currentOrders = currentOrders.filter(order => order.orderStatus === 'PENDING');


    // 2. Filter by Payment Status
    if (filterPaymentStatus !== 'all') {
      currentOrders = currentOrders.filter(order => order.paymentStatus === filterPaymentStatus);
    }

    // 3. Filter by Shipping Status
    if (filterShippingStatus !== 'all') {
      currentOrders = currentOrders.filter(order => order.shippingStatus === filterShippingStatus);
    }

    // 4. Sort
    if (sortColumn) {
      currentOrders.sort((a, b) => {
        let valA: any;
        let valB: any;

        if (sortColumn === 'customerName') {
          valA = a.customer.name.toLowerCase();
          valB = b.customer.name.toLowerCase();
        } else if (sortColumn === 'orderDate') {
          valA = new Date(a.orderDate).getTime();
          valB = new Date(b.orderDate).getTime();
        } else if (sortColumn === 'totalAmount') {
          valA = a.totalAmount;
          valB = b.totalAmount;
        } else {
          valA = (a as any)[sortColumn];
          valB = (b as any)[sortColumn];
        }

        if (typeof valA === 'string' && typeof valB === 'string') {
          return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        } else if (typeof valA === 'number' && typeof valB === 'number') {
          return sortOrder === 'asc' ? valA - valB : valB - valA;
        }
        return 0;
      });
    }

    return currentOrders;
  }, [orders, searchTerm, filterPaymentStatus, filterShippingStatus, sortColumn, sortOrder]);

  // Pagination Logic
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrdersForDisplay = filteredAndSortedOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredAndSortedOrders.length / ordersPerPage);

  const paginate = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // --- Action Handlers (Simulated) ---
  const handleViewDetails = (order: Order) => { // NEW: Now takes the full order object
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const handleEditOrder = (id: number) => {
    showNotification(`Editing Pending Order ID: ${id}`, 'info');
    // In a real app: navigate(`/admin/orders/edit/${id}`);
  };

  const initiateDelete = (order: Order) => {
    setOrderToDelete(order);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (orderToDelete) {
      // Simulate API call for deletion
      showNotification(`Pending Order ID: ${orderToDelete.id} deleted successfully! (Simulated)`, 'success');
      setOrders(prev => prev.filter(order => order.id !== orderToDelete.id));
      setShowDeleteModal(false);
      setOrderToDelete(null);
      // In a real app: re-fetch data or update state after successful deletion
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setOrderToDelete(null);
  };

  // Status badge helper function (re-using from OrderListPage)
  const getStatusBadge = (status: string, type: 'order' | 'payment' | 'shipping') => {
    let colorClass = 'bg-gray-100 text-gray-800'; // Default
    switch (type) {
      case 'order':
        switch (status) {
          case 'DELIVERED': colorClass = 'bg-green-100 text-green-800'; break;
          case 'SHIPPED': colorClass = 'bg-blue-100 text-blue-800'; break;
          case 'PROCESSING': colorClass = 'bg-yellow-100 text-yellow-800'; break;
          case 'PENDING': colorClass = 'bg-purple-100 text-purple-800'; break;
          case 'CANCELLED': colorClass = 'bg-red-100 text-red-800'; break;
          case 'REFUND': colorClass = 'bg-indigo-100 text-indigo-800'; break;
        }
        break;
      case 'payment':
        switch (status) {
          case 'PAID': colorClass = 'bg-green-100 text-green-800'; break;
          case 'UNPAID': colorClass = 'bg-red-100 text-red-800'; break;
          case 'REFUND_PENDING': colorClass = 'bg-yellow-100 text-yellow-800'; break;
          case 'REFUNDED': colorClass = 'bg-indigo-100 text-indigo-800'; break;
        }
        break;
      case 'shipping':
        switch (status) {
          case 'DELIVERED': colorClass = 'bg-green-100 text-green-800'; break;
          case 'IN_TRANSIT': colorClass = 'bg-blue-100 text-blue-800'; break;
          case 'OUT_FOR_DELIVERY': colorClass = 'bg-orange-100 text-orange-800'; break;
          case 'READY_FOR_DISPATCH': colorClass = 'bg-purple-100 text-purple-800'; break;
        }
        break;
    }
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${colorClass}`}>
        {status.replace(/_/g, ' ')}
      </span>
    );
  };


  return (
    <AdminLayout>
      <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Pending Order List</h1>
          <button
            onClick={() => showNotification('Navigate to Create Order page (not implemented)', 'info')}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center justify-center font-semibold text-sm shadow-md"
          >
            <FaPlus className="mr-2" /> Create Order (Simulated)
          </button>
        </div>

        {/* Search & Filter Controls */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6">
          <div className="relative w-full sm:w-1/3">
            <input
              type="text"
              placeholder="Search by ID, Customer, Product, Amount..."
              className="w-full p-2 pl-9 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>

          <div className="flex flex-wrap gap-4 w-full sm:w-auto justify-end">
            {/* Filter by Payment Status */}
            <select
              value={filterPaymentStatus}
              onChange={(e) => {
                setFilterPaymentStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 bg-white"
            >
              <option value="all">All Payment Statuses</option>
              <option value="PAID">Paid</option>
              <option value="UNPAID">Unpaid</option>
              <option value="REFUND_PENDING">Refund Pending</option>
              <option value="REFUNDED">Refunded</option>
            </select>

            {/* Filter by Shipping Status */}
            <select
              value={filterShippingStatus}
              onChange={(e) => {
                setFilterShippingStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 bg-white"
            >
              <option value="all">All Shipping Statuses</option>
              <option value="READY_FOR_DISPATCH">Ready for Dispatch</option>
              <option value="IN_TRANSIT">In Transit</option>
              <option value="OUT_FOR_DELIVERY">Out for Delivery</option>
              <option value="DELIVERED">Delivered</option>
            </select>

            {/* Sort By Dropdown */}
            <select
              value={sortColumn}
              onChange={(e) => {
                const newSortColumn = e.target.value;
                setSortColumn(newSortColumn);
                setSortOrder(newSortColumn === sortColumn ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'desc'); // Default new sort to desc for date
                setCurrentPage(1);
              }}
              className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 bg-white"
            >
              <option value="orderDate">Sort by Date {sortColumn === 'orderDate' && (sortOrder === 'asc' ? '↑' : '↓')}</option>
              <option value="totalAmount">Sort by Amount {sortColumn === 'totalAmount' && (sortOrder === 'asc' ? '↑' : '↓')}</option>
              <option value="customerName">Sort by Customer Name {sortColumn === 'customerName' && (sortOrder === 'asc' ? '↑' : '↓')}</option>
            </select>
          </div>
        </div>
      </div>

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {loading ? (
        <p className="text-center text-lg py-10 text-gray-600">Loading pending orders...</p>
      ) : filteredAndSortedOrders.length === 0 ? (
        <p className="text-center text-lg py-10 text-gray-600">
          No pending orders found matching your criteria.
        </p>
      ) : (
        <>
          {/* Order Table */}
          <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order Date & Time
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Amount
                  </th>
                  {/* Order Status column removed as it's always PENDING */}
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Shipping Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentOrdersForDisplay.map((order) => (
                  <tr key={order.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      #{order.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.customer.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(order.orderDate).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      ₹{order.totalAmount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getStatusBadge(order.paymentStatus, 'payment')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getStatusBadge(order.shippingStatus, 'shipping')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                      <button
                        onClick={() => handleViewDetails(order)} // NEW: Pass the full order object
                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                        title="View Details"
                      >
                        <FaEye className="inline" />
                      </button>
                      <button
                        onClick={() => handleEditOrder(order.id)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Edit Order"
                      >
                        <FaEdit className="inline" />
                      </button>
                      <button
                        onClick={() => initiateDelete(order)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Order"
                      >
                        <FaTrashAlt className="inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-center items-center gap-2 mt-6">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => paginate(i + 1)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                  currentPage === i + 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                } transition-colors`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm"
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && orderToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-8 rounded-lg shadow-2xl max-w-md w-full border border-gray-300 transform transition-all duration-300 scale-100 ease-out">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Confirm Deletion</h3>
            <p className="text-gray-700 mb-6">Are you sure you want to delete this pending order from <span className="font-semibold text-red-600">"{orderToDelete.customer.name}"</span> with ID <span className="font-semibold text-red-600">#{orderToDelete.id}</span>?</p>
            <p className="text-gray-500 text-sm mb-6">This action cannot be undone.</p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={cancelDelete}
                className="px-6 py-2 rounded-lg border border-gray-400 text-gray-700 hover:bg-gray-100 transition-colors font-semibold shadow-sm"
              >
                Cancel
              </button>
              {/* Kept red for delete */}
              <button
                onClick={confirmDelete}
                className="px-6 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors font-semibold shadow-md"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Details Modal */} {/* NEW: Conditional rendering of OrderDetailsModal */}
      {showDetailsModal && selectedOrder && (
        <OrderDetailsModal order={selectedOrder} onClose={() => setShowDetailsModal(false)} />
      )}
    </AdminLayout>
  );
};

export default PendingOrder;